import React from "react";
import DatePicker from "../../../../Common/DatePicker";

export default function ProvidentFundRatesSection({
  rates,
  effectiveDate,
  onEffectiveDateChange,
  onRateChange,
  onSave,
  onViewHistory,
  minEffectiveDate = "2026-04-01",
  embedded = false, // inside a modal: no card chrome / title / History / Save (host owns them)
}) {
  const pf = rates?.pf || {};
  const employerSplitTotal =
    (Number(pf.epf) || 0) +
    (Number(pf.eps) || 0) +
    (Number(pf.adminCharges) || 0) +
    (Number(pf.edliRate) || 0);

  return (
    <div
      className={
        embedded
          ? ""
          : "rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-6 shadow-sm"
      }
    >
      {!embedded && (
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[hsl(var(--ds-fg))]">Provident Fund (PF) Rate</h3>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={onViewHistory}
              className="rounded bg-[hsl(var(--ds-muted))] px-3 py-1 text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            >
              History
            </button>
            <button
              type="button"
              onClick={onSave}
              className="inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 rounded px-3 py-1 text-sm hover:opacity-95"
            >
              Save
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 rounded border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-accent))] p-3">
        <label className="mb-1 block text-sm font-medium text-[hsl(var(--ds-secondary))]">
          Effective Date *
        </label>
        <DatePicker
          value={effectiveDate}
          onChange={(e) => onEffectiveDateChange(e.target.value)}
          min={minEffectiveDate}
          minErrorText="Effective date cannot be before service commencement / statutory baseline"
          className="rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
        />
      </div>

      <div className={embedded ? "space-y-4" : "max-h-96 space-y-4 overflow-y-auto"}>
        <div>
          <h4 className="mb-2 text-sm font-medium text-[hsl(var(--ds-fg))]">Basic PF Rates</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-fg))]">
                Employee Share (%)
              </label>
              <input
                type="number"
                value={pf.employeeShare}
                onChange={(e) => onRateChange("employeeShare", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-fg))]">
                Employer Share (%)
              </label>
              <input
                type="number"
                value={pf.employerShare}
                onChange={(e) => onRateChange("employerShare", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-fg))]">
                PF Wage Ceiling (₹)
              </label>
              <input
                type="number"
                value={pf.pfWageCeiling ?? pf.ceiling}
                onChange={(e) => onRateChange("pfWageCeiling", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                Ceiling PF: employee + employer 12% capped here; EPF = 12% − EPS
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-fg))]">
                EPS Wage Ceiling (₹)
              </label>
              <input
                type="number"
                value={pf.epsWageCeiling ?? 15000}
                onChange={(e) => onRateChange("epsWageCeiling", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">Employer EPS capped at this ceiling × EPS rate</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-fg))]">
                EDLI Wage Ceiling (₹)
              </label>
              <input
                type="number"
                value={pf.edliWageCeiling ?? 15000}
                onChange={(e) => onRateChange("edliWageCeiling", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">EDLI capped at this ceiling × EDLI rate</p>
            </div>
            <div className="col-span-2 rounded border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
              <label htmlFor="pf-prorate-ceiling" className="flex items-start space-x-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  id="pf-prorate-ceiling"
                  checked={!!pf.prorateCeiling}
                  onChange={(e) => onRateChange("prorateCeiling", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))] focus:ring-[hsl(var(--ds-ring))]"
                />
                <div>
                  <span className="text-xs font-semibold text-[hsl(var(--ds-fg))]">
                    Prorate Ceiling
                  </span>
                  <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                    When enabled, the configured PF ceiling is prorated based on the applicable calendar days and payable days for the payroll period.
                  </p>
                  <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                    If rates change mid-month, each period uses its own ceiling for its days in Process Payroll.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="rounded border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-accent))] p-3">
          <h4 className="mb-2 text-sm font-medium text-[hsl(var(--ds-secondary))]">
            PF Bifurcation (Employer Share)
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-secondary))]">
                EPF (Employer) — residual
              </label>
              <input
                type="number"
                step="0.01"
                value={pf.epf}
                onChange={(e) => onRateChange("epf", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ds-secondary))]">
                Calculated as Employer 12% − EPS (not used as a direct % multiply)
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-secondary))]">
                EPS Rate (%) — Pension 8.33%
              </label>
              <input
                type="number"
                step="0.01"
                value={pf.eps}
                onChange={(e) => onRateChange("eps", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ds-secondary))]">Employee Pension Scheme</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-secondary))]">
                Admin Charges (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={pf.adminCharges}
                onChange={(e) => onRateChange("adminCharges", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-secondary))]">
                Admin Charges Minimum (₹ / month)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={pf.adminChargesMinimum ?? 500}
                onChange={(e) => onRateChange("adminChargesMinimum", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
              <p className="mt-1 text-xs text-[hsl(var(--ds-secondary))]">
                EPFO establishment minimum (default ₹500). Applied on PF statement totals.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[hsl(var(--ds-secondary))]">
                EDLI Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={pf.edliRate}
                onChange={(e) => onRateChange("edliRate", e.target.value)}
                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-[hsl(var(--ds-secondary))]">
            <strong>Total Employer Share:</strong> {employerSplitTotal.toFixed(2)}%
            {Math.abs(employerSplitTotal - (Number(pf.employerShare) || 0)) > 0.01 && (
              <span className="ml-2 text-red-600">(≠ employer share {pf.employerShare}%)</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
